<template>
  <div>
    <modal-adicionar-usuario
      :idConta="conta.id"
      :unidades="unidades"
      :sltGrupo="sltGrupo"
      :grupos="grupos"
      :gruposPermissoes="gruposPermissoes"
      :contasDisponiveis="contasDisponiveis"
      :quantidadeUsuarios="usuarios.length"
      @usuario:cadastrado="usuarioCadastrado"
      v-if="isAdmin || hasPermission('gerenciar-usuario')"
    >
    </modal-adicionar-usuario>

    <div class="usuarios-tabela">
      <b-table hover small show-empty :fields="cabecalho" :items="usuarios">
        <template #empty>
          <p class="text-center mb-0">Nenhum registro encontrado.</p>
        </template>

        <template #cell(actions)="row">
          <modal-alterar-senha
            :id="row.item.id"
            url="usuario/alterar-senha"
            titulo="Alterar Senha do Usuário"
            :exigirSenhaAtual="false"
          ></modal-alterar-senha>

          <modal-editar-usuario
            :usuario="row.item"
            :unidades="unidades"
            :sltGrupo="sltGrupo"
            :grupos="grupos"
            :gruposPermissoes="gruposPermissoes"
            :contasDisponiveis="contasDisponiveis"
            @usuario:atualizado="usuarioAtualizado"
          ></modal-editar-usuario>

          <b-button
            variant="light-primary"
            size="sm"
            class="btn-icon"
            v-b-tooltip.noninteractive
            title="Excluir"
            @click="excluir(row.item.id, row.index)"
          >
            <i class="fas fa-trash"></i>
          </b-button>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";

import ModalEditarUsuario from "./ModalEditarUsuario";
import ModalAdicionarUsuario from "./ModalAdicionarUsuario";
import ModalAlterarSenha from "@/view/components/ModalAlterarSenha";

export default {
  props: {
    conta: Object
  },

  data() {
    return {
      usuarios: [],
      unidades: [],
      sltGrupo: [],
      grupos: [],
      gruposPermissoes: {},
      contasDisponiveis: []
    };
  },

  components: {
    "modal-editar-usuario": ModalEditarUsuario,
    "modal-adicionar-usuario": ModalAdicionarUsuario,
    "modal-alterar-senha": ModalAlterarSenha
  },

  mounted() {
    this.abrir();
  },

  methods: {
    abrir() {
      ApiService.post("conta/usuarios", { idConta: this.conta.id })
        .then(async (response) => {
          this.usuarios = response.data.data.usuarios;
          this.unidades = response.data.data.unidades;
          this.sltGrupo = response.data.data.sltGrupo;
          this.grupos = response.data.data.grupos;
          this.gruposPermissoes = response.data.data.gruposPermissoes;
          await this.carregarContasDisponiveis(
            response.data.data.contasDisponiveis || []
          );
        })
        .catch(() => {
          this.$emit("erro-permissao");
        });
    },

    async carregarContasDisponiveis(contasApi) {
      const contasAtivasApi = contasApi.filter((conta) => conta.status === "A");
      this.contasDisponiveis = contasAtivasApi;

      if (!this.checkSuperAdmin) {
        return;
      }

      const porPagina = 200;

      try {
        const obterPagina = (pagina) =>
          ApiService.get(
            `central-contas?pagina=${pagina}&porPagina=${porPagina}`,
            "",
            false
          ).then(({ data }) => data.data);

        const primeiraPagina = await obterPagina(1);
        const totalPaginas = Math.max(
          1,
          Math.ceil(primeiraPagina.total / porPagina)
        );
        const paginas = [primeiraPagina.contas];

        if (totalPaginas > 1) {
          const demaisPaginas = await Promise.all(
            Array.from({ length: totalPaginas - 1 }, (_, indice) =>
              obterPagina(indice + 2).then((resultado) => resultado.contas)
            )
          );

          paginas.push(...demaisPaginas);
        }

        this.contasDisponiveis = paginas
          .flat()
          .reduce((contas, principal) => {
            contas.push(principal, ...(principal.subcontas || []));
            return contas;
          }, [])
          .filter((conta) => conta.status === "A");
      } catch {
        this.contasDisponiveis = contasAtivasApi;
      }
    },

    usuarioCadastrado(usuarioCadastrado) {
      const index = this.usuarios.length;

      this.usuarios.splice(index, 0, usuarioCadastrado);
    },

    usuarioAtualizado(usuarioAtualizado) {
      const index = this.usuarios.findIndex(
        (usuario) => usuario.id === usuarioAtualizado.id
      );

      this.usuarios.splice(index, 1, usuarioAtualizado);
    },

    excluir(id, index) {
      const self = this;

      Swal.fire({
        title: "Deseja realmente excluir este usuário?",
        showCancelButton: true,
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
        customClass: {
          cancelButton: "btn btn-secondary",
          confirmButton: "btn btn-danger"
        },
        buttonsStyling: false
      }).then((result) => {
        if (result.value) {
          ApiService.post("usuario/excluir", {
            idUsuario: id,
            idConta: this.conta.id
          }).then(() => {
            Swal.fire({
              title: "MENSAGEM",
              text: "Usuário excluído com sucesso!",
              icon: "success",
              heightAuto: false
            });

            self.usuarios.splice(index, 1);
          });
        }
      });
    }
  },

  computed: {
    ...mapGetters(["isAdmin", "hasPermission", "checkSuperAdmin"]),

    cabecalho() {
      const cabecalho = [
        {
          key: "nome",
          label: "Nome",
          sortable: true,
          class: "align-middle"
        },
        {
          key: "email",
          label: "Email",
          sortable: true,
          class: "align-middle"
        },
        {
          key: "nome_grupo",
          label: "Grupo",
          sortable: true,
          class: "align-middle"
        }
      ];

      if (this.isAdmin || this.hasPermission("gerenciar-usuario")) {
        cabecalho.push({
          key: "actions",
          label: "Ações",
          sortable: false,
          class: "align-middle text-center"
        });
      }

      return cabecalho;
    }
  }
};
</script>

<style>
.p-multiselect-panel {
  z-index: 1065 !important;
}
</style>

<style lang="scss" scoped>
.usuarios-tabela {
  border: 1px solid #eeecf3;
  border-radius: 12px;
  overflow: hidden;

  ::v-deep table {
    margin-bottom: 0;
  }

  ::v-deep thead th {
    background: #f7f4fc;
    color: #472282;
    border-bottom: 1px solid #eeecf3;
    font-weight: 700;
    font-size: 0.82rem;
  }

  ::v-deep tbody tr:hover {
    background: #f7f4fc;
  }

  ::v-deep td,
  ::v-deep th {
    border-color: #eeecf3;
  }
}
</style>
